import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { plainToInstance } from "class-transformer";
import { Observable } from "rxjs";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { lastValueFrom } from "rxjs/internal/lastValueFrom";
import { apiCallWrapper } from "../../api/api.util";
import {
  FormTemplateApi,
  FormTemplateOutput,
  FormTemplateValidationOutput,
  ICreateUpdateFormTemplateInput,
  QuestionnaireOverview,
} from "../../api/forms/form-template.api";
import { HasIdOrExternalValueOrExternalIdOrServiceName } from "../../api/generic.api";
import { FormTemplate } from "../../model/forms/form-template.model";
import { BaseService } from "../base.service";
import { ToastService } from "../toast.service";
import { TFormTemplateCounts } from "./contract/form-template.contract";

@Injectable({
  providedIn: "root",
})
export class FormTemplateService extends BaseService {
  private formTemplatesSubject = new BehaviorSubject<FormTemplate[] | null>(null);

  constructor(private toastService: ToastService, private formTemplateApi: FormTemplateApi) {
    super();
  }

  public async getFormTemplates(): Promise<FormTemplate[]> {
    await this.setFormTemplates();

    return this.formTemplatesSubject.value || [];
  }

  public async getCounts(): Promise<TFormTemplateCounts> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.getCounts(), {
          toastService: this.toastService,
          action: "Obtaining FormTemplate counts",
        })
      );

      return data;
    } catch (error) {
      throw error;
    }
  }

  public createPreview(
    formTemplateUUID: string,
    previewOffline: boolean,
    companyExternalId?: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      apiCallWrapper(
        this.formTemplateApi.createPreview(formTemplateUUID, previewOffline, companyExternalId),
        {
          toastService: this.toastService,
          action: "Create form-template preview",
        }
      ).subscribe({
        next: (result) => {
          resolve(result);
        },
        error: (error) => reject(error),
      });
    });
  }

  public async validateFormTemplate(uuid: string): Promise<FormTemplateValidationOutput> {
    return await lastValueFrom(
      apiCallWrapper(this.formTemplateApi.validateFormTemplate(uuid), {
        toastService: this.toastService,
        action: "Validate questionnaire",
      })
    );
  }

  public async changeDraftStatus(uuid: string, isDraft: boolean): Promise<{ success: boolean }> {
    try {
      const result = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.changeDraftStatus(uuid, isDraft), {
          toastService: this.toastService,
          action: "Change form-template draft status",
        })
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  public async changeArchiveStatus(uuid: string): Promise<{ success: boolean }> {
    try {
      const result = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.changeArchiveStatus(uuid), {
          toastService: this.toastService,
          action: "Change form-template archive status",
        })
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  public async hasAnswersByUUID(uuid: string): Promise<{ hasAnswers: boolean }> {
    try {
      const result = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.hasAnswersByUUID(uuid), {
          toastService: this.toastService,
          action: "Check if form-template has answers",
        })
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Fetches the form templates from the API and sets them to the formTemplatesSubject on application load
   * @returns
   */
  public async setFormTemplates(): Promise<boolean> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.getAll(), {
          toastService: this.toastService,
          action: "Obtaining FormTemplate details",
        })
      );

      const formTemplates = this.inflateModel<
        FormTemplate,
        FormTemplate & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(FormTemplate, data);

      this.formTemplatesSubject.next(formTemplates.rows);

      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  public async getForDropDown(): Promise<FormTemplate[]> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.getForDropDown(), {
          toastService: this.toastService,
          action: "Obtaining Questionnaire details for dropdown",
        })
      );

      const formTemplates = this.inflateModel<
        FormTemplate,
        FormTemplate & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(FormTemplate, data);

      return formTemplates.rows;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }

  /**
   * Makes the API call to fetch the form-template by UUID
   * The output structure is form-template -> sections [-> sections] -> questions.
   *
   * @param {string} uuid the FormTemplateUUID to fetch
   * @returns {Promise<FormTemplate>} a promise that resolves to the FormTemplate
   */
  public getFormTemplateByUUID(uuid: string): Promise<FormTemplate> {
    return new Promise((resolve, reject) => {
      apiCallWrapper(this.formTemplateApi.getByUUID(uuid), {
        toastService: this.toastService,
        action: "Fetch form-template",
      }).subscribe({
        next: (result) => {
          const formTemplate = plainToInstance(FormTemplate, result);
          resolve(formTemplate);
        },
        error: (error) => reject(error),
      });
    });
  }

  public getFormTemplateVersionsById(id: number): Promise<FormTemplate[]> {
    return new Promise((resolve, reject) => {
      apiCallWrapper(this.formTemplateApi.getFormTemplateVersionsById(id), {
        toastService: this.toastService,
        action: "Fetch form-template version",
      }).subscribe({
        next: (result) => {
          const formTemplates: FormTemplate[] = [];
          if (result) {
            for (let index = 0; index < result.length; index++) {
              const formTemplate = plainToInstance(FormTemplate, result[index]);
              formTemplates.push(formTemplate);
            }
          }
          resolve(formTemplates);
        },
        error: (error) => reject(error),
      });
    });
  }

  public doesFormTemplateHaveQuestionGroups(uuid: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      apiCallWrapper(this.formTemplateApi.doesFormTemplateHaveQuestionGroups(uuid), {
        toastService: this.toastService,
        action: "Does form-template have any questions that are in a group",
      }).subscribe({
        next: (result: boolean) => {
          resolve(result);
        },
        error: (error) => reject(error),
      });
    });
  }

  public copyFormTemplate(
    formTemplateUUID: string,
    formTemplateName: string,
    copyAllQuestions?: boolean,
    copyLayoutAndBrandingSettings?: boolean
  ): Observable<any> {
    return this.formTemplateApi.copyFormTemplate({
      formTemplateUUID,
      formTemplateName,
      copyAllQuestions,
      copyLayoutAndBrandingSettings,
    });
  }

  public async getFormTemplateByUuid(uuid: string): Promise<FormTemplateOutput> {
    const result = await lastValueFrom(
      apiCallWrapper(this.formTemplateApi.getFormTemplateByUuid(uuid), {
        toastService: this.toastService,
        action: "Fetching form-template by UUID",
      })
    );
    return result;
  }

  public async createOrUpdateFormTemplate(
    input: ICreateUpdateFormTemplateInput
  ): Promise<{ id: number; uuid: string }> {
    const result = await lastValueFrom(
      apiCallWrapper(this.formTemplateApi.createOrUpdateFormTemplate(input), {
        toastService: this.toastService,
        action: "Creating/updating form-template",
      })
    );
    return result;
  }

  public getIntroImageSignedUrl(id: number): Promise<string | null> {
    return lastValueFrom(this.formTemplateApi.getIntroImageSignedUrl(id));
  }

  public async copyAndLinkFormTemplate(uuid: string): Promise<{ newFormTemplateUUID: string }> {
    try {
      const result = await lastValueFrom(
        apiCallWrapper(this.formTemplateApi.copyAndLinkFormTemplate(uuid), {
          toastService: this.toastService,
          action: "Copy and link form-template",
        })
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Fetches read-only questionnaire overview metadata for the details modal.
   * @param uuid Questionnaire UUID.
   * @returns Questionnaire overview details.
   */
  public async getQuestionnaireOverview(uuid: string): Promise<QuestionnaireOverview> {
    return await lastValueFrom(
      apiCallWrapper(this.formTemplateApi.getQuestionnaireOverview(uuid), {
        toastService: this.toastService,
        action: "Fetching questionnaire overview",
      })
    );
  }
}
